import { ImagePlus, Paperclip, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { useApp } from "../../context/AppContext";
import { api } from "../../lib/api";
import type { Contact, CustomerOrder, CustomerOrderStatus } from "../../types";
import { Button } from "../ui";

export const customerStatusOptions = [
  ["PROSPECT", "新客户"],
  ["QUALIFYING", "需求判断中"],
  ["ACTIVE", "重点推进中"],
  ["PAUSED", "暂缓联系"],
  ["UNSUITABLE", "暂不适合"]
] as const;

export const orderStatusOptions: Array<[CustomerOrderStatus, string]> = [
  ["INQUIRY", "收到询盘"],
  ["REQUIREMENT_CONFIRMED", "需求确认"],
  ["QUOTING", "报价中"],
  ["SAMPLING", "打样中"],
  ["SAMPLE_CONFIRMATION", "样品确认"],
  ["DEPOSIT_PENDING", "等待定金"],
  ["PRODUCTION", "生产中"],
  ["QUALITY_CHECK", "验货中"],
  ["BALANCE_PENDING", "等待尾款"],
  ["SHIPPING", "发货中"],
  ["COMPLETED", "已完成"],
  ["PAUSED", "已暂停"],
  ["CANCELLED", "已取消"]
];

// 订单生命周期引导：每个阶段的推荐下一步 + 标准动作清单
export const orderLifecycleGuide: Record<
  CustomerOrderStatus,
  { next: CustomerOrderStatus; actions: string[] }
> = {
  INQUIRY: { next: "REQUIREMENT_CONFIRMED", actions: ["回复询盘，确认客户真实需求", "确认面料、款式、数量、交期要求"] },
  REQUIREMENT_CONFIRMED: { next: "QUOTING", actions: ["整理完整报价单发给客户", "确认价格条款、MOQ 与交期"] },
  QUOTING: { next: "SAMPLING", actions: ["跟进客户对报价的反馈", "推进打样安排"] },
  SAMPLING: { next: "SAMPLE_CONFIRMATION", actions: ["安排打样并跟进进度", "寄样后跟踪客户反馈"] },
  SAMPLE_CONFIRMATION: { next: "DEPOSIT_PENDING", actions: ["确认样品修改意见", "发送 PI / 收款信息"] },
  DEPOSIT_PENDING: { next: "PRODUCTION", actions: ["跟进定金到账", "确认后安排生产排期"] },
  PRODUCTION: { next: "QUALITY_CHECK", actions: ["跟进生产进度与关键节点", "提前预约验货时间"] },
  QUALITY_CHECK: { next: "BALANCE_PENDING", actions: ["完成验货并出报告", "发送尾款付款信息"] },
  BALANCE_PENDING: { next: "SHIPPING", actions: ["跟进尾款到账", "安排出货与物流"] },
  SHIPPING: { next: "COMPLETED", actions: ["提供提单/物流单给客户", "确认客户收货"] },
  COMPLETED: { next: "COMPLETED", actions: ["归档客户反馈", "沉淀可复用的经验"] },
  PAUSED: { next: "INQUIRY", actions: ["记录暂停原因", "设定恢复跟进日期"] },
  CANCELLED: { next: "INQUIRY", actions: ["记录取消原因", "沉淀教训，避免同类问题"] }
};

function ModalShell({
  title,
  subtitle,
  children,
  onClose
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <section
        className="modal modal--wide"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="modal__header">
          <div><h2>{title}</h2><p>{subtitle}</p></div>
          <button className="modal__close" onClick={onClose} aria-label="关闭"><X size={20} /></button>
        </header>
        {children}
      </section>
    </div>
  );
}

const COUNTRY_TIMEZONES: Array<[string, string]> = [
  ["美国", "America/New_York"], ["英国", "Europe/London"], ["法国", "Europe/Paris"],
  ["德国", "Europe/Paris"], ["意大利", "Europe/Paris"], ["西班牙", "Europe/Madrid"],
  ["欧洲", "Europe/Paris"], ["迪拜", "Asia/Dubai"], ["阿联酋", "Asia/Dubai"],
  ["澳大利亚", "Australia/Sydney"], ["加拿大", "America/Toronto"], ["日本", "Asia/Tokyo"],
  ["韩国", "Asia/Seoul"], ["中国", "Asia/Shanghai"], ["香港", "Asia/Hong_Kong"],
  ["印度", "Asia/Kolkata"], ["新加坡", "Asia/Singapore"], ["马来西亚", "Asia/Kuala_Lumpur"],
  ["墨西哥", "America/Mexico_City"], ["巴西", "America/Sao_Paulo"], ["南非", "Africa/Johannesburg"],
  ["土耳其", "Europe/Istanbul"], ["俄罗斯", "Europe/Moscow"], ["沙特", "Asia/Riyadh"],
  ["印尼", "Asia/Jakarta"], ["越南", "Asia/Ho_Chi_Minh"], ["泰国", "Asia/Bangkok"],
  ["菲律宾", "Asia/Manila"], ["波兰", "Europe/Warsaw"], ["乌克兰", "Europe/Kiev"],
  ["以色列", "Asia/Jerusalem"], ["智利", "America/Santiago"],
  ["阿根廷", "America/Argentina/Buenos_Aires"], ["新西兰", "Pacific/Auckland"],
  ["荷兰", "Europe/Amsterdam"], ["瑞士", "Europe/Zurich"], ["瑞典", "Europe/Stockholm"],
  ["挪威", "Europe/Oslo"], ["比利时", "Europe/Brussels"], ["葡萄牙", "Europe/Lisbon"],
  ["爱尔兰", "Europe/Dublin"], ["奥地利", "Europe/Vienna"], ["丹麦", "Europe/Copenhagen"],
  ["芬兰", "Europe/Helsinki"], ["希腊", "Europe/Athens"], ["捷克", "Europe/Prague"],
  ["埃及", "Africa/Cairo"], ["尼日利亚", "Africa/Lagos"], ["肯尼亚", "Africa/Nairobi"],
  ["巴基斯坦", "Asia/Karachi"], ["孟加拉", "Asia/Dhaka"], ["斯里兰卡", "Asia/Colombo"],
  ["秘鲁", "America/Lima"], ["哥伦比亚", "America/Bogota"], ["哥斯达黎加", "America/Costa_Rica"]
];

export function tzNameOf(country: string) {
  const trimmed = country.trim();
  if (!trimmed) return null;
  return COUNTRY_TIMEZONES.find(([name]) => trimmed.includes(name))?.[1] ?? null;
}

function TimezoneHint({ tz, country }: { tz: string; country: string }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, []);
  const fmt = (timeZone: string) =>
    new Intl.DateTimeFormat("zh-CN", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone }).format(now);
  const local = fmt(tz);
  const beijing = fmt("Asia/Shanghai");
  const diff = Number(local.split(":")[0]) - Number(beijing.split(":")[0]);
  const diffText = diff === 0 ? "与北京时间相同" : diff > 0 ? `比北京快 ${diff} 小时` : `比北京慢 ${-diff} 小时`;
  return (
    <span className="tz-hint">{country.trim()}当地现在是 <b>{local}</b>（北京 {beijing}，{diffText}）——发消息注意对方作息</span>
  );
}

export function CustomerModal({
  customer,
  onClose
}: {
  customer?: Contact;
  onClose: () => void;
}) {
  const { mutate } = useApp();
  const [saving, setSaving] = useState(false);
  const [countryField, setCountryField] = useState(customer?.country ?? "");

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const payload = {
      name: String(form.get("name") ?? ""),
      organization: String(form.get("organization") ?? ""),
      country: String(form.get("country") ?? ""),
      contactChannel: String(form.get("contactChannel") ?? ""),
      channelAccount: String(form.get("channelAccount") ?? ""),
      customerStatus: String(form.get("customerStatus") ?? "PROSPECT"),
      importance: String(form.get("importance") ?? "B"),
      mainProducts: String(form.get("mainProducts") ?? ""),
      purchaseSeason: String(form.get("purchaseSeason") ?? ""),
      annualPurchase: String(form.get("annualPurchase") ?? ""),
      priceSensitivity: String(form.get("priceSensitivity") ?? ""),
      moqAcceptance: String(form.get("moqAcceptance") ?? ""),
      commStyle: String(form.get("commStyle") ?? ""),
      paymentHabit: String(form.get("paymentHabit") ?? ""),
      riskTags: String(form.get("riskTags") ?? ""),
      context: String(form.get("context") ?? ""),
      nextAction: String(form.get("nextAction") ?? ""),
      nextFollowup: String(form.get("nextFollowup") ?? "")
    };
    setSaving(true);
    const saved = await mutate(
      () => customer
        ? api.update("contacts", customer.id, payload)
        : api.create("contacts", payload),
      customer ? "客户资料已更新" : "客户已加入跟单列表"
    );
    setSaving(false);
    if (saved) onClose();
  };

  return (
    <ModalShell
      title={customer ? "编辑客户" : "登记新客户"}
      subtitle="记录判断、下一步和跟进日期，聊天软件只负责沟通，这里负责不遗忘。"
      onClose={onClose}
    >
      <form onSubmit={(event) => void submit(event)}>
        <div className="modal__body form-grid form-grid--two">
          <label className="field"><span>客户称呼 <b>*</b></span><input name="name" required autoFocus defaultValue={customer?.name} placeholder="姓名或业务称呼" /></label>
          <label className="field"><span>公司/组织</span><input name="organization" defaultValue={customer?.organization} placeholder="公司名称" /></label>
          <label className="field"><span>国家或市场</span><input name="country" defaultValue={customer?.country} placeholder="例如：美国、法国" onChange={(event) => setCountryField(event.target.value)} /></label>
          {tzNameOf(countryField) && <div className="field field--full"><TimezoneHint tz={tzNameOf(countryField)!} country={countryField} /></div>}
          <label className="field"><span>主要联系渠道</span><select name="contactChannel" defaultValue={customer?.contact_channel ?? "WhatsApp"}>{["WhatsApp", "微信", "邮箱", "Alibaba", "电话", "其他"].map((item) => <option key={item}>{item}</option>)}</select></label>
          <label className="field"><span>账号/联系方式</span><input name="channelAccount" defaultValue={customer?.channel_account} placeholder="号码、邮箱或平台账号" /></label>
          <label className="field"><span>客户重要性</span><select name="importance" defaultValue={customer?.importance ?? "B"}><option value="S">S · 核心客户</option><option value="A">A · 重要客户</option><option value="B">B · 常规客户</option><option value="C">C · 低优先/观察</option></select></label>
          <label className="field"><span>当前关系状态</span><select name="customerStatus" defaultValue={customer?.customer_status ?? "PROSPECT"}>{customerStatusOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          <label className="field"><span>主营产品</span><input name="mainProducts" defaultValue={customer?.main_products} placeholder="例如：女装、针织、童装" /></label>
          <label className="field"><span>采购季节</span><input name="purchaseSeason" defaultValue={customer?.purchase_season} placeholder="例如：春夏、秋冬、全年" /></label>
          <label className="field"><span>年度采购预算</span><input name="annualPurchase" defaultValue={customer?.annual_purchase} placeholder="例如：USD 200k（未确认可留空）" /></label>
          <label className="field"><span>价格敏感度</span><input name="priceSensitivity" defaultValue={customer?.price_sensitivity} placeholder="例如：中高，接受精品价位" /></label>
          <label className="field"><span>MOQ 接受能力</span><input name="moqAcceptance" defaultValue={customer?.moq_acceptance} placeholder="例如：50-100件起、接受柔性快反" /></label>
          <label className="field"><span>沟通风格</span><input name="commStyle" defaultValue={customer?.comm_style} placeholder="例如：邮件正式、WhatsApp简短、回复快" /></label>
          <label className="field"><span>付款习惯</span><input name="paymentHabit" defaultValue={customer?.payment_habit} placeholder="例如：30%定金+70%尾款、TT" /></label>
          <label className="field"><span>风险标签</span><input name="riskTags" defaultValue={customer?.risk_tags} placeholder="例如：压价狠、账期长、易流失" /></label>
          <label className="field"><span>下次跟进日期</span><input type="date" name="nextFollowup" defaultValue={customer?.next_followup ?? ""} /></label>
          <label className="field field--full"><span>客户背景与判断</span><textarea name="context" rows={3} defaultValue={customer?.context} placeholder="客户卖什么、主要市场、价格敏感度、合作判断等" /></label>
          <label className="field field--full"><span>下一步要做什么</span><textarea name="nextAction" rows={2} defaultValue={customer?.next_action} placeholder="例如：周五前发送3款卫衣报价和面料色卡" /></label>
        </div>
        <div className="modal__footer"><Button type="button" variant="ghost" onClick={onClose}>取消</Button><Button type="submit" disabled={saving}>{saving ? "正在保存…" : customer ? "保存修改" : "登记客户"}</Button></div>
      </form>
    </ModalShell>
  );
}

function parseImages(order?: CustomerOrder) {
  if (!order) return [];
  try {
    const parsed = JSON.parse(order.images_json) as unknown;
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === "string") : [];
  } catch {
    return [];
  }
}

interface OrderFile {
  url: string;
  name: string;
}

function parseFiles(order?: CustomerOrder): OrderFile[] {
  if (!order) return [];
  try {
    const parsed = JSON.parse(order.files_json ?? "[]") as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (item): item is OrderFile =>
        typeof item === "object" && item !== null && typeof (item as OrderFile).url === "string"
    );
  } catch {
    return [];
  }
}

function fileToBase64(file: File) {
  return new Promise<string>((resolvePromise, rejectPromise) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? "");
      const comma = result.indexOf(",");
      resolvePromise(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => rejectPromise(new Error("读取文件失败"));
    reader.readAsDataURL(file);
  });
}

async function compressImage(file: File) {
  if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
    throw new Error("仅支持 JPG、PNG 或 WebP 图片");
  }
  const bitmap = await createImageBitmap(file);
  const maxSide = 1440;
  const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const context = canvas.getContext("2d");
  if (!context) throw new Error("当前浏览器无法处理图片");
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  const blob = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((value) => value ? resolve(value) : reject(new Error("图片压缩失败")), "image/webp", 0.82)
  );
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("图片读取失败"));
    reader.readAsDataURL(blob);
  });
  return { mimeType: blob.type, dataBase64: dataUrl.split(",")[1] ?? "" };
}

export function OrderModal({
  contacts,
  order,
  presetContactId,
  onClose
}: {
  contacts: Contact[];
  order?: CustomerOrder;
  presetContactId?: string;
  onClose: () => void;
}) {
  const { mutate, notify } = useApp();
  const [saving, setSaving] = useState(false);
  const [existingImages, setExistingImages] = useState(() => parseImages(order));
  const [files, setFiles] = useState<Array<{ file: File; preview: string }>>([]);
  const [existingFiles, setExistingFiles] = useState<OrderFile[]>(() => parseFiles(order));
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [statusField, setStatusField] = useState<CustomerOrderStatus>(order?.status ?? "INQUIRY");
  const imageCount = existingImages.length + files.length;
  const fileCount = existingFiles.length + pendingFiles.length;

  const addFiles = (selected: FileList | null) => {
    if (!selected) return;
    const remaining = Math.max(0, 6 - imageCount);
    const next = Array.from(selected).slice(0, remaining);
    if (selected.length > remaining) notify("每张订单最多保留6张图片", "info");
    setFiles((current) => [...current, ...next.map((file) => ({ file, preview: URL.createObjectURL(file) }))]);
  };

  const removeFile = (index: number) => {
    setFiles((current) => {
      const target = current[index];
      if (target) URL.revokeObjectURL(target.preview);
      return current.filter((_, itemIndex) => itemIndex !== index);
    });
  };

  const addDocFiles = (selected: FileList | null) => {
    if (!selected) return;
    const remaining = Math.max(0, 20 - fileCount);
    const next = Array.from(selected).slice(0, remaining);
    if (selected.length > remaining) notify("每张订单最多保留20个附件", "info");
    setPendingFiles((current) => [...current, ...next]);
  };

  const removeDocFile = (index: number) => {
    setPendingFiles((current) => current.filter((_, itemIndex) => itemIndex !== index));
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setSaving(true);
    try {
      const uploaded: string[] = [];
      for (const item of files) {
        const prepared = await compressImage(item.file);
        const result = await api.uploadOrderImage(prepared);
        uploaded.push(result.url);
      }
      const uploadedFiles: OrderFile[] = [];
      for (const file of pendingFiles) {
        const dataBase64 = await fileToBase64(file);
        const result = await api.uploadOrderFile({
          name: file.name,
          mimeType: file.type,
          dataBase64
        });
        uploadedFiles.push(result);
      }
      const payload = {
        contactId: String(form.get("contactId") ?? ""),
        orderCode: String(form.get("orderCode") ?? ""),
        title: String(form.get("title") ?? ""),
        images: [...existingImages, ...uploaded],
        files: [...existingFiles, ...uploadedFiles],
        requirements: String(form.get("requirements") ?? ""),
        amount: String(form.get("amount") ?? ""),
        currency: String(form.get("currency") ?? "USD"),
        quantity: form.get("quantity") ? Number(form.get("quantity")) : null,
        status: String(form.get("status") ?? "INQUIRY"),
        nextAction: String(form.get("nextAction") ?? ""),
        nextFollowup: String(form.get("nextFollowup") ?? ""),
        note: String(form.get("note") ?? "")
      };
      const saved = await mutate(
        () => order
          ? api.update("customer-orders", order.id, payload)
          : api.create("customer-orders", payload),
        order ? "订单资料已更新" : "订单已加入跟单系统"
      );
      if (saved) onClose();
    } catch (caught) {
      notify(caught instanceof Error ? caught.message : "图片上传失败", "error");
    } finally {
      setSaving(false);
    }
  };

  const amount = useMemo(() => order && order.amount_minor > 0 ? (order.amount_minor / 100).toFixed(2) : "", [order]);

  return (
    <ModalShell title={order ? "编辑订单" : "登记订单"} subtitle="把图片、要求、金额和下一步放在同一张订单卡上。" onClose={onClose}>
      <form onSubmit={(event) => void submit(event)}>
        <div className="modal__body form-grid form-grid--two">
          <label className="field"><span>所属客户 <b>*</b></span><select name="contactId" required defaultValue={order?.contact_id ?? presetContactId ?? ""}><option value="" disabled>请选择客户</option>{contacts.map((contact) => <option key={contact.id} value={contact.id}>{contact.name}{contact.organization ? ` · ${contact.organization}` : ""}</option>)}</select></label>
          <label className="field"><span>订单编号</span><input name="orderCode" defaultValue={order?.order_code} placeholder="客户PO号或内部编号" /></label>
          <label className="field field--full"><span>款式/订单名称 <b>*</b></span><input name="title" required autoFocus defaultValue={order?.title} placeholder="例如：法国客户 320gsm 拉链卫衣" /></label>
          <div className="field field--full"><span>款式图片（最多6张）</span><div className="order-image-editor">{existingImages.map((src) => <figure key={src}><img src={src} alt="订单款式" /><button type="button" onClick={() => setExistingImages((current) => current.filter((item) => item !== src))} aria-label="移除图片"><X size={14} /></button></figure>)}{files.map((item, index) => <figure key={item.preview}><img src={item.preview} alt={item.file.name} /><button type="button" onClick={() => removeFile(index)} aria-label="移除图片"><X size={14} /></button></figure>)}{imageCount < 6 && <label className="order-image-add"><ImagePlus size={22} /><span>添加图片</span><input type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={(event) => addFiles(event.target.files)} /></label>}</div></div>
          <div className="field field--full"><span>附件文件（最多20个）</span><div className="order-file-editor">{existingFiles.map((item) => <span className="order-file-chip" key={item.url}><Paperclip size={13} />{item.name || "附件"}<button type="button" onClick={() => setExistingFiles((current) => current.filter((entry) => entry.url !== item.url))} aria-label="移除文件"><X size={13} /></button></span>)}{pendingFiles.map((file, index) => <span className="order-file-chip" key={`${file.name}-${index}`}><Paperclip size={13} />{file.name}<button type="button" onClick={() => removeDocFile(index)} aria-label="移除文件"><X size={13} /></button></span>)}{fileCount < 20 && <label className="order-file-add"><Paperclip size={15} /><span>添加文件</span><input type="file" multiple onChange={(event) => addDocFiles(event.target.files)} /></label>}</div></div>
          <label className="field field--full"><span>客户具体要求</span><textarea name="requirements" rows={5} defaultValue={order?.requirements} placeholder="面料、克重、颜色、尺码、印花/绣花、包装、交期、验货标准等" /></label>
          <label className="field"><span>订单金额</span><input type="number" min="0" step="0.01" name="amount" defaultValue={amount} placeholder="0.00" /></label>
          <label className="field"><span>币种</span><select name="currency" defaultValue={order?.currency ?? "USD"}>{["USD", "CNY", "EUR", "HKD", "JPY"].map((item) => <option key={item}>{item}</option>)}</select></label>
          <label className="field"><span>数量</span><input type="number" min="1" name="quantity" defaultValue={order?.quantity ?? ""} placeholder="件数" /></label>
          <label className="field"><span>当前进度</span><select name="status" defaultValue={order?.status ?? "INQUIRY"} onChange={(event) => setStatusField(event.target.value as CustomerOrderStatus)}>{orderStatusOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
            {orderLifecycleGuide[statusField] && (
              <span className="lifecycle-hint">
                <b>下一步：{orderStatusOptions.find(([value]) => value === orderLifecycleGuide[statusField].next)?.[1]}</b>
                <i>{orderLifecycleGuide[statusField].actions.join(" · ")}</i>
              </span>
            )}
          </label>
          <label className="field"><span>下次跟进日期</span><input type="date" name="nextFollowup" defaultValue={order?.next_followup ?? ""} /></label>
          <label className="field field--full"><span>下一步动作</span><textarea name="nextAction" rows={2} defaultValue={order?.next_action} placeholder="例如：明天下午确认印花尺寸并更新PI" /></label>
          <label className="field field--full"><span>补充备注</span><textarea name="note" rows={2} defaultValue={order?.note} placeholder="付款条款、特殊风险、沟通偏好等" /></label>
        </div>
        <div className="modal__footer"><Button type="button" variant="ghost" onClick={onClose}>取消</Button>{order && <Button type="button" variant="ghost" onClick={() => setExistingImages([])} icon={<Trash2 size={15} />}>清空图片</Button>}<Button type="submit" disabled={saving}>{saving ? "正在保存图片与订单…" : order ? "保存订单" : "登记订单"}</Button></div>
      </form>
    </ModalShell>
  );
}
